import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useNavigate } from "react-router-dom"
import { ROUTES } from "@/routes/path"
import { Database, Search, X } from "lucide-react"
import React from "react"
import resourceTypes from "@/lib/data/resources.json"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { useToast } from "@/hooks/useToast"

// Removed background color function since we want no background

interface ResourceType {
  id: string
  name: string
  icon: string // Now contains URL to SVG icon
  category: string
  most_popular?: number
  fields?: any[] // Configuration fields for the resource
}

export default function CreateResourcePage() {
  const [searchQuery, setSearchQuery] = useState("")
  const navigate = useNavigate()
  const { toast } = useToast()

  // Create "Most Popular" category from resources with most_popular flag, sorted by popularity
  const mostPopularResources = resourceTypes
    .filter((resource) => resource.most_popular && resource.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => (a.most_popular || 0) - (b.most_popular || 0))

  // Group resources by category and filter by search query
  const filteredResources = resourceTypes.reduce(
    (acc, resource) => {
      // Filter by search query
      if (!resource.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return acc
      }

      // Skip most popular resources as they'll be handled separately
      if (resource.most_popular) {
        return acc
      }

      // Determine category display name
      let categoryName = ""
      switch (resource.category) {
        case "database":
          categoryName = "Databases"
          break
        case "api":
          categoryName = "APIs"
          break
        case "saas":
          categoryName = "SaaS Integrations"
          break
        default:
          categoryName = "Other"
      }

      // Add to category
      if (!acc[categoryName]) {
        acc[categoryName] = []
      }
      acc[categoryName].push(resource)

      return acc
    },
    {} as Record<string, ResourceType[]>
  )

  // Add "Most Popular" category at the beginning if it has resources
  if (mostPopularResources.length > 0) {
    filteredResources["Most Popular"] = mostPopularResources
  }

  const handleResourceSelect = (resource: ResourceType) => {
    // Check if resource has fields defined
    if (!resource.fields || resource.fields.length === 0) {
      toast({
        title: "Datasource not available",
        description: "This datasource is not available. Please try again later.",
        variant: "info",
      })
      return
    }

    navigate(ROUTES.DASHBOARD.RESOURCES.CONFIGURE(resource.id))
  }

  return (
    <div className="mx-auto max-w-7xl p-6">
      {/* Header with close button */}
      <div className="mb-8 flex justify-between">
        <div>
          <h1 className="mb-2 text-3xl font-bold text-gray-900 dark:text-white">Connect a datasource</h1>
          <p className="text-gray-600 dark:text-gray-400">Select a sample datasource or connect your own.</p>
        </div>
        <div className="mb-4 items-center justify-between">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <X className="h-6 w-6" />
          </Button>
        </div>
      </div>

      <Alert variant="info" className="mb-5" closable>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="size-5 stroke-blue-800"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
          />
        </svg>

        <AlertDescription>
          When connecting resources, your passwords are AES-256 encrypted and we never store any of your data.
        </AlertDescription>
      </Alert>

      {/* Search bar */}
      <div className="mb-8">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
          <Input
            placeholder="Search for datasources"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-4"
          />
        </div>
      </div>

      {/* Resource categories */}
      <div className="space-y-12">
        {Object.entries(filteredResources)
          .sort(([a], [b]) => {
            // Always show "Most Popular" first
            if (a === "Most Popular") return -1
            if (b === "Most Popular") return 1
            return a.localeCompare(b)
          })
          .map(([category, resources]) => (
            <div key={category}>
              <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">{category}</h2>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-5">
                {resources.map((resource) => {
                  const hasFields = resource.fields && resource.fields.length > 0

                  return (
                    <button
                      key={resource.id}
                      onClick={() => handleResourceSelect(resource)}
                      className={`flex items-center gap-3 rounded-lg p-3 transition-all ${
                        hasFields ? "hover:bg-gray-50 dark:hover:bg-gray-800" : "opacity-60"
                      }`}
                    >
                      <img
                        src={resource.icon}
                        alt={resource.name}
                        className={`h-6 w-6 ${!hasFields ? "grayscale" : ""}`}
                      />
                      <span
                        className={`text-sm font-medium ${
                          hasFields ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400"
                        }`}
                      >
                        {resource.name}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
      </div>

      {/* No results */}
      {Object.keys(filteredResources).length === 0 && (
        <div className="py-12 text-center">
          <Database className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">No datasources found</h3>
          <p className="text-gray-600 dark:text-gray-400">Try adjusting your search terms.</p>
        </div>
      )}
    </div>
  )
}
